import { Button, Flex, spaces, Text } from '@electricjs/arc';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useStoredState } from 'common/useStoredState';
import { useUpdateUserLanguageMutation } from '@/redux/slices/userApiSlice';

const BASE_LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr-CA', label: 'FR' },
];

const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation('components');
  const { spanishLanguageSwitcher } = useFlags();

  const languages = spanishLanguageSwitcher
    ? [...BASE_LANGUAGES, { code: 'es', label: 'ES' }]
    : BASE_LANGUAGES;

  const [language, setLanguage] = useStoredState('user-language', 'en');
  const [updateUserLanguage] = useUpdateUserLanguageMutation();

  const handleLanguageChange = (lang: string) => {
    // Update i18n language
    i18n.changeLanguage(lang);

    // Save to localStorage (fallback)
    setLanguage(lang);

    // Save to backend (to persist across devices, reflect on e-mails and on other backend translations)
    updateUserLanguage({ language: lang })
      .unwrap()
      .catch(error => {
        // Fail silently - localStorage will still work as fallback
        console.error('Failed to save language preference:', error);
      });
  };

  const currentLanguage = i18n.language || language;

  const isActiveLanguage = (code: string) =>
    currentLanguage === code || currentLanguage.startsWith(code + '-');

  return (
    <Flex
      px={spaces.space_m}
      pb={spaces.space_m}
      alignItems="center"
      gap={spaces.space_2xs}>
      <Text variant="label-small">{t('languageSwitcher.label')}</Text>
      <Flex gap={spaces.space_2xs} flexGrow="1" justifyContent="space-evenly">
        {languages.map(lang => (
          <Button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            variant={isActiveLanguage(lang.code) ? 'fill' : 'text'}
            intent={isActiveLanguage(lang.code) ? 'primary' : 'neutral'}
            size="small"
            id={lang.code}>
            {lang.label}
          </Button>
        ))}
      </Flex>
    </Flex>
  );
};

export default LanguageSwitcher;
